"use client";

import React from "react";
import { Clock, FileCheck, Palette, Truck, ClipboardList, CircleCheck as CheckCircle2, CalendarDays, ShieldCheck, Sparkles, ArrowRight, House as Home, HeartHandshake, DollarSign, Droplets, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface TimelineFactor {
  title: string;
  duration: string;
  category: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
interface LifeTrigger {
  title: string;
  signal: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const TIMELINE_FACTORS: TimelineFactor[] = [{
  title: "Design finalization",
  duration: "2 - 4 Weeks",
  category: "Phase 1",
  description: "Architectural layouts, 3D renderings, plumbing rough-in schematics, and luxury finish selections locked in prior to procurement.",
  icon: Palette
}, {
  title: "City of Brookhaven permitting",
  duration: "3 - 6 Weeks",
  category: "Phase 2",
  description: "Submittal of structural, plumbing, and electrical plan sets to Brookhaven municipal building authorities for thorough compliance.",
  icon: FileCheck
}, {
  title: "Material ordering lead times",
  duration: "4 - 8 Weeks",
  category: "Phase 3",
  description: "Procurement of custom double vanities, imported porcelain tile, frameless glass enclosures, and bespoke brass plumbing trim.",
  icon: Truck
}, {
  title: "Build & municipal inspections",
  duration: "4 - 7 Weeks",
  category: "Phase 4",
  description: "Rough-in trades, pre-sheetrock inspections, waterproofing flood tests, tile installation, fixture trims, and final building sign-off.",
  icon: ClipboardList
}];
const LIFE_TRIGGERS: LifeTrigger[] = [{
  title: "Outdated bathroom finishes",
  signal: "Deteriorating grout, tired brass, or worn cultured marble",
  description: "Cramped corner tubs and leaky shower pans no longer match the quality of your home or your daily comfort standards.",
  icon: Layers
}, {
  title: "Aging-in-place & accessibility",
  signal: "Need for zero-barrier entry and reinforced safety",
  description: "Curbless walk-in showers, ADA-compliant blocking, anti-slip porcelain surfaces, and comfort-height floating vanities designed for longevity.",
  icon: HeartHandshake
}, {
  title: "Bought an older Brookhaven home",
  signal: "Historic charm paired with 1960s–1980s plumbing limits",
  description: "Modernizing vintage Brookhaven ranch and colonial homes with updated supply lines, tankless heaters, and expanded master footprints.",
  icon: Home
}, {
  title: "Preparing to sell your residence",
  signal: "Seeking maximum equity returns in competitive local real estate",
  description: "Primary bath renovations in Brookhaven regularly recoup substantial equity while dramatically reducing days on market.",
  icon: DollarSign
}, {
  title: "Want modern luxury fixtures",
  signal: "Craving a personal wellness sanctuary and spa amenities",
  description: "Thermostatic dual rain shower heads, integrated steam systems, freestanding soaking tubs, and heated floor systems.",
  icon: Droplets
}];
export default function TimelineTriggers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="remodeltimelineandtriggers" data-nav="light" ref={ref} className={`bg-background text-foreground py-20 lg:py-28 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* SECTION 1: TIMELINE FACTORS */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-primary/40 text-primary uppercase tracking-wider text-xs">
                Brookhaven Project Guidance
              </Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-primary" />
                Predictable Timelines
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-foreground leading-[1.15]">
              Understanding your bathroom remodeling timeline.
            </h2>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Precision planning prevents mid-project surprises. From local Brookhaven municipal zoning reviews to artisan tile curing, each stage is managed on an intentional schedule.
            </p>

            <div className="relative mt-2 overflow-hidden rounded-xl border border-border shadow-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_remodel_brookhaven.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_remodel_brookhaven.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_remodel_brookhaven.png-wvc-sizes" alt="Contemporary master bathroom renovation in Brookhaven with custom vanity and walk-in shower" className="w-full h-72 sm:h-80 object-cover" loading="lazy" />
              <div className="absolute bottom-0 inset-x-0 bg-background/90 backdrop-blur-xs p-4 border-t border-border flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Standard Primary Bath</p>
                  <p className="text-xs text-muted-foreground">Typical overall span: 9 to 14 weeks from design sign-off</p>
                </div>
                <ShieldCheck className="w-5 h-5 text-primary shrink-0" />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TIMELINE_FACTORS.map((factor, i) => {
              const IconComponent = factor.icon;
              return <Card key={factor.title} data-index={i} className="border border-border bg-card shadow-xs hover:shadow-md transition-shadow flex flex-col justify-between">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="inline-flex p-2 rounded-md bg-muted text-primary">
                          <IconComponent className="w-5 h-5" />
                        </span>
                        <Badge variant="secondary" className="text-xs font-mono">
                          {factor.duration}
                        </Badge>
                      </div>
                      <span className="text-xs font-medium text-primary tracking-wide">{factor.category}</span>
                      <CardTitle className="text-lg font-serif text-card-foreground">
                        {factor.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {factor.description}
                      </CardDescription>
                    </CardContent>
                  </Card>;
            })}
            </div>

            <div className="p-4 rounded-lg bg-muted/60 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs sm:text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                Permits filed directly with Brookhaven Community Development Department.
              </span>
              <span className="font-medium text-foreground">Zero guesswork for homeowners</span>
            </div>
          </div>
        </div>

        <Separator className="my-16 lg:my-24" />

        {/* SECTION 2: LIFE-MOMENT TRIGGERS */}
        <div className="space-y-12">
          <div className="max-w-3xl">
            <Badge variant="outline" className="border-primary/40 text-primary uppercase tracking-wider text-xs mb-3">
              Homeowner Check
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-foreground leading-[1.15]">
              Is it time to remodel your bathroom?
            </h2>
            <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
              Bathrooms endure the highest humidity, traffic, and plumbing stress in any home. If your space reflects any of these pivotal life moments, a planned elevation is your next right step.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* Left list of triggers */}
            <div className="lg:col-span-7 flex flex-col gap-4">
              {LIFE_TRIGGERS.map((trigger, i) => {
              const TriggerIcon = trigger.icon;
              return <div key={trigger.title} data-index={i} className="p-5 rounded-xl border border-border bg-card shadow-2xs hover:border-primary/50 transition-all flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="p-2.5 rounded-lg bg-muted text-primary shrink-0 self-start">
                      <TriggerIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="font-serif text-lg font-medium text-card-foreground">
                          {trigger.title}
                        </h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-accent text-accent-foreground font-mono">
                          Trigger {i + 1}
                        </span>
                      </div>
                      <p className="text-xs font-medium text-primary">
                        {trigger.signal}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed pt-1">
                        {trigger.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Right photo & CTA container */}
            <div className="lg:col-span-5 flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm overflow-hidden">
              <div className="space-y-4">
                <div className="relative rounded-lg overflow-hidden border border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/freestanding_tub_renovation_bathroom.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/freestanding_tub_renovation_bathroom.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/freestanding_tub_renovation_bathroom.png-wvc-sizes" alt="Freestanding soaking tub and floor-to-ceiling porcelain tile bathroom remodel in a Brookhaven home" className="w-full h-64 object-cover" loading="lazy" />
                  <div className="absolute top-3 left-3">
                    <Badge variant="default" className="bg-secondary text-secondary-foreground shadow-xs">
                      <Sparkles className="w-3 h-3 mr-1 text-primary" />
                      Turnkey Renovation
                    </Badge>
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <h3 className="text-xl font-serif text-card-foreground">
                    Plan your Brookhaven bathroom transformation
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Brookhaven Elevation Renovations coordinates every architectural blueprint, historic preservation compliance, and master craftsperson on your behalf.
                  </p>
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-border flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="w-full sm:w-auto flex-1 font-medium">
                  <Link to="/ContactRequestAnEstimatePage">
                    Schedule an estimate
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                  <Link to="/OurProjectsPage">
                    View local gallery
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
}